import { useMemo, type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { intl } from '@trello/i18n';
import { checkId, idCache } from '@trello/id-cache';
import { HelpIcon } from '@trello/nachos/icons/help';
import { InformationIcon } from '@trello/nachos/icons/information';
import {
  getBoardShortLinkFromPathname,
  getLocation,
  RouteId,
  useIsActiveRoute,
} from '@trello/router';
import type { ShortcutsPageTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { useIsCollapsibleListsEnabled } from 'app/src/components/CollapsedListsState';

import styles from './Shortcuts.less';

interface ShortcutProps {
  readonly keys: string[];
  readonly messageId: string;
  readonly helpPageUrl?: string;
  readonly children?: object | undefined;
}

interface LabelTableProps {
  readonly keys: string[][];
}

const Shortcut = (props: PropsWithChildren<ShortcutProps>) => (
  <div className={styles.item}>
    <h3 className={styles.itemHeader}>
      {intl.formatMessage({
        id: `templates.shortcuts.${props.messageId}`,
        // since we're displaying the header based on the passed in prop,
        // we can't assign a default message
        // eslint-disable-next-line formatjs/enforce-default-message
        defaultMessage: '',
        description: 'The header describing the shortcut',
      })}
      {props.helpPageUrl && (
        <a className={styles.helpIcon} href={props.helpPageUrl} target="_blank">
          <HelpIcon size="small" />
        </a>
      )}
    </h3>
    <div className={styles.itemKeys}>
      {props.keys.map((key, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <span className={styles.keyboardKey} key={index}>
          {key}
        </span>
      ))}
    </div>
    <div className={styles.itemDesc}>{props.children}</div>
  </div>
);

const LabelTable = (props: LabelTableProps) => (
  <table>
    <thead>
      <tr>
        <th>
          <FormattedMessage
            id="templates.shortcuts.key"
            defaultMessage="Key"
            description="The key of the label"
          />
        </th>
        <th>
          <FormattedMessage
            id="templates.shortcuts.label-color"
            defaultMessage="Label color"
            description="The color that corresponds to the label key"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      {props.keys.map((key, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <tr key={index}>
          <td>{key[0]}</td>
          <td>
            {intl.formatMessage({
              id: `templates.shortcuts.${key[1]}`,
              // eslint-disable-next-line formatjs/enforce-default-message
              defaultMessage: '',
              description: 'A description of a label color',
            })}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const labelKeys = [
  ['1', 'green'],
  ['2', 'yellow'],
  ['3', 'orange'],
  ['4', 'red'],
  ['5', 'purple'],
  ['6', 'blue'],
  ['7', 'sky'],
  ['8', 'lime'],
  ['9', 'pink'],
  ['0', 'black'],
];

export const Shortcuts = () => {
  const isBoardRoute = useIsActiveRoute(RouteId.BOARD);
  const boardId = useMemo<string | undefined>(() => {
    if (!isBoardRoute) {
      return;
    }
    const { pathname } = getLocation();
    const boardShortLink = getBoardShortLinkFromPathname(pathname);
    if (!boardShortLink) {
      return;
    }
    return checkId(boardShortLink)
      ? boardShortLink
      : idCache.getBoardId(boardShortLink);
  }, [isBoardRoute]);

  // A falsy value here is fine; the fragment read will simply fail.
  const isCollapsibleListsEnabled = useIsCollapsibleListsEnabled(boardId);

  return (
    <div
      className={styles.shortcutsContainer}
      data-testid={getTestId<ShortcutsPageTestIds>('shortcuts-container')}
    >
      <div className={styles.header}>
        <InformationIcon
          dangerous_className={styles.icon}
          size="large"
          color={token('color.icon', '#44546F')}
        />
        <h2 id="js-dialog-title">
          <FormattedMessage
            id="templates.shortcuts.keyboard-shortcuts"
            defaultMessage="Keyboard shortcuts"
            description="The header of the overlay for keyboard shortcuts in Trello"
          />
        </h2>
      </div>
      <div className={styles.list}>
        <hr role="presentation" />
        <Shortcut
          keys={['←', '↓ / J', '↑ / K', '→']}
          messageId="navigate-cards"
        >
          <FormattedMessage
            id="templates.shortcuts.pressing-the-arrow-keys-will-select-adjacent-cards-on-a-board-pressing-j-will-select-the-card-below-the-current-card-pressing-k-will-select-the-card-above-the-current-card"
            defaultMessage="Pressing the arrow keys will select adjacent cards on a board. Pressing “j” will select the card below the current card. Pressing “k” will select the card above the current card."
            description="A description of keyboard shortcuts for navigating cards"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['← / K', '→ / J']} messageId="navigate-card-backs">
          <FormattedMessage
            id="templates.shortcuts.pressing-the-left-and-right-arrow-keys-with-open-adjacent-cards-on-a-board"
            defaultMessage="Pressing the left and right arrow keys will open adjacent cards in a list. Pressing “j” or the right arrow will open the card below the current card. Pressing “k” or the left arrow will open the card above the current card."
            description="A description of keyboard shortcuts for navigating the cardback"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Command/Control', 'C / V']} messageId="copy-card">
          <FormattedMessage
            id="templates.shortcuts.when-hovering-over-a-card-pressing-control-c-or-command-c-will-copy-the-card-to-your-clipboard-pasting-while-hovering-over-a-list-will-copy-the-card-to-the-list"
            defaultMessage="When hovering over a card, pressing Control + C (Windows) or Command + C (Mac) will copy the card to your clipboard. Pasting by pressing Control + V (Windows) or Command + V (Mac) while hovering over a list will copy the card to the list. This will work between different boards."
            description="A description of the keyboard shortcut for copying a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Command/Control', 'X / V']} messageId="move-card">
          <FormattedMessage
            id="templates.shortcuts.when-hovering-over-a-card-pressing-control-x-ord-command-x-will-copy-the-card-to-your-clipboard-pasting-while-hovering-over-a-list-will-move-the-card-to-the-list"
            defaultMessage="When hovering over a card, pressing Control + X (Windows) or Command + X (Mac) will copy card to your clipboard. Pasting by pressing Control + V (Windows) or Command + V (Mac) while hovering over a list will move the card to the list. This will work between different boards."
            description="A description of the keyboard shortcut for moving a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut
          keys={['Command/Control', 'V']}
          messageId="insert-new-card-with-text"
        >
          <FormattedMessage
            id="templates.shortcuts.pressing-control-v-will-insert-new-card-with-text"
            defaultMessage="When on a board, create a new card with a copied link or text with “Control/Command + V”."
            description="A description of the keyboard shortcut for inserting a new card with text"
            tagName="p"
          />
        </Shortcut>

        <Shortcut
          keys={['Command/Control', 'V']}
          messageId="add-link-as-attachment"
        >
          <FormattedMessage
            id="templates.shortcuts.when-viewing-a-card-control-v-will-add-pasted-url-as-an-attachment"
            defaultMessage="When viewing a card, attach a copied link with “Control/Command + V”."
            description="A description of the shortcut for adding a link to a card as an attachment"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['[']} messageId="toggle-sidebar">
          <FormattedMessage
            id="templates.shortcuts.pressing-open-bracket-shortcut-opens-board-navigation"
            defaultMessage="Pressing “[” opens and closes the boards navigation sidebar menu."
            description="A description of the shortcut for toggling the board sidebar"
            tagName="p"
          />
        </Shortcut>
        <Shortcut keys={[']']} messageId="toggle-board-menu">
          <FormattedMessage
            id="templates.shortcuts.pressing-]-will-collapse-or-expand-the-board-menu-the-sidebar-on-the-right"
            defaultMessage="Pressing “]” will collapse or expand the board menu, the sidebar on the right."
            description="A description of the shortcut to toggle the board menu"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['B']} messageId="open-header-boards-menu">
          <FormattedMessage
            id="templates.shortcuts.pressing-b-opens-the-boards-menu-in-the-header-you-can-search-for-boards-and-navigate-boards-with-the-up-and-down-arrows-pressing-enter-with-a-board-selected-will-open-it"
            defaultMessage="Pressing “b” lets you quickly search for boards. Navigate search results with the up and down arrows. Pressing enter with a board selected will open it."
            description="A description of the shortcut to open the header to search boards"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['/']} messageId="focus-search-box">
          <FormattedMessage
            id="templates.shortcuts.pressing-puts-the-cursor-in-the-search-box-in-the-header"
            defaultMessage="Pressing “/” puts the cursor in the search box in the header."
            description="A description of the shortcut to focus the search box in the header"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['C']} messageId="archive-card">
          <FormattedMessage
            id="templates.shortcuts.pressing-c-will-archive-a-card"
            defaultMessage="Pressing “c” will archive a card."
            description="A description of the shortcut to archive a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['D']} messageId="due-date">
          <FormattedMessage
            id="templates.shortcuts.pressing-d-will-open-the-due-date-picker-for-a-card"
            defaultMessage="Pressing “d” will open the due date picker for a card."
            description="A description of the shortcut to open the due date picker for a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['-']} messageId="add-checklist">
          <FormattedMessage
            id="templates.shortcuts.pressing-dash-adds-a-checklist"
            defaultMessage="Pressing “-” will add a checklist to a card."
            description="A description of the shortcut to add a checklist to a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['E']} messageId="quick-edit-mode">
          <FormattedMessage
            id="templates.shortcuts.if-hovering-over-a-card-pressing-e-will-open-quick-edit-mode-which-lets-you-quickly-edit-the-title-and-other-card-attributes"
            defaultMessage="If hovering over a card, pressing “e” will open quick edit mode, which lets you quickly edit the title and other card attributes."
            description="A description of the shortcut to open quick edit mode for a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Esc']} messageId="close-menu-cancel-editing">
          <FormattedMessage
            id="templates.shortcuts.pressing-esc-will-close-an-open-dialog-window-or-pop-over-or-cancel-edits-and-comments-you-are-composing"
            defaultMessage="Pressing “esc” will close an open dialog window or pop-over or cancel edits and comments you are composing."
            description="A description of the shortcut to cancel edits and comments you are composing"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Command/Control', 'Enter']} messageId="save-text">
          <FormattedMessage
            id="templates.shortcuts.meta-enter-copy"
            defaultMessage="Pressing Control + Enter (Windows) or Command + Enter (Mac) will save any text you are writing. This works when writing or editing comments, editing the card title, list title, description, and other things."
            description="A description of the shortcut to save any text you are writing"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Enter']} messageId="open-card">
          <FormattedMessage
            id="templates.shortcuts.pressing-enter-will-open-the-currently-selected-card-pressing-shift-enter-while-submitting-a-card-will-open-it-immediately-after-creating-it"
            defaultMessage="Pressing “enter” will open the currently selected card. Pressing “shift + enter” while submitting a card will open it immediately after creating it."
            description="A description of the shortcut to open the currently selected card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['F']} messageId="open-card-filter-menu">
          <FormattedMessage
            id="templates.shortcuts.use-f-to-open-the-card-filter-menu-the-search-by-title-input-is-automatically-focused"
            defaultMessage="Use “f” to open the card filter menu. The search by title input is automatically focused."
            description="A description of the shortcut to open the card filter menu"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['L']} messageId="label">
          <FormattedMessage
            id="templates.shortcuts.pressing-l-opens-a-pop-over-of-the-available-labels-clicking-a-label-will-add-or-remove-it-from-the-card"
            defaultMessage="Pressing “l” opens a pop-over of the available labels. Clicking a label will add or remove it from the card."
            description="A description of the shortcut to open the labels popover for a card"
            tagName="p"
          />

          <FormattedMessage
            id="templates.shortcuts.pressing-one-of-the-following-number-keys-will-apply-or-remove-that-label"
            defaultMessage="Pressing one of the following number keys, will apply or remove that label."
            description="A description of the shortcut to apply a label to a card"
            tagName="p"
          />

          <LabelTable keys={labelKeys} />
        </Shortcut>

        <Shortcut keys={[';']} messageId="label-text">
          <FormattedMessage
            id="templates.shortcuts.shows-or-hides-label-text"
            defaultMessage="Pressing the semicolon key “;” shows or hides the names of labels on a board. You can also click any label on a board to toggle this."
            description="A description of the shortcut to show or hide labels on a board"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['M']} messageId="add-remove-members">
          <FormattedMessage
            id="templates.shortcuts.pressing-m-opens-the-add-remove-members-menu-clicking-a-member-s-avatar-will-assign-or-unassign-that-person"
            defaultMessage="Pressing “m” opens the add / remove members menu. Clicking a member’s avatar will assign or unassign that person."
            description="A description of the shortcut to add or remove members from a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['N']} messageId="insert-new-card">
          <FormattedMessage
            id="templates.shortcuts.pressing-n-opens-a-pop-over-that-allows-you-to-add-a-card-after-the-currently-selected-card"
            defaultMessage="Pressing “n” opens a pop-over that allows you to add a card after the currently selected card, or in an empty list."
            description="A description of the shortcut to add a new card"
            tagName="p"
          />
        </Shortcut>

        {/* Note: interpolate the key in case we decide to change it: */}
        {isCollapsibleListsEnabled && (
          <>
            <Shortcut keys={['\\']} messageId="collapse-list">
              <FormattedMessage
                id="templates.shortcuts.collapse-list-description"
                defaultMessage="Hover or focus on a list and press “{key}” to collapse or expand it."
                description="A description of the shortcut to collapse or expand a list"
                values={{ key: '\\' }}
                tagName="p"
              />
            </Shortcut>
            <Shortcut keys={['Shift', '\\']} messageId="collapse-all-lists">
              <FormattedMessage
                id="templates.shortcuts.collapse-all-lists-description"
                defaultMessage="Press “Shift + {key}” to collapse or expand all of the lists on a board at the same time."
                description="A description of the shortcut to collapse or expand all the lists on a board"
                values={{ key: '\\' }}
                tagName="p"
              />
            </Shortcut>
          </>
        )}

        <Shortcut
          keys={[',', '.', '<', '>']}
          messageId="move-card-to-adjacent-list"
        >
          <p>
            {
              /*
               * We're doing an inline ''.replace() here because the templates contains HTML-encoded "<" and ">"
               * characters (that we can't easily change because the templates are validated by libxmljs). If/when this
               * problem re-surfaces we should consider refactoring the i18n lib to better support it.
               */
              intl
                .formatMessage({
                  id: 'templates.shortcuts.pressing-period-or-comma-will-move-a-card-to-the-adjacent-left-or-right-list',
                  defaultMessage:
                    //the default string has a double whitespace, what do you want from me
                    // eslint-disable-next-line formatjs/no-multiple-whitespaces
                    'Pressing “,” or “.” will move a card to the bottom of the adjacent left or right list.  Pressing the left or right angle brackets (&lt; and &gt;) will move a card to the top of the adjacent left or right list.',
                  description:
                    'A description of the shortcut to move the card to adjacent lists',
                })
                .replace('&lt;', '<')
                .replace('&gt;', '>')
            }
          </p>
        </Shortcut>

        <Shortcut keys={['Q']} messageId="my-cards-filter">
          <FormattedMessage
            id="templates.shortcuts.pressing-the-q-key-toggles-the-cards-assigned-to-me-filter"
            defaultMessage="Pressing the “q” key toggles the “cards assigned to me” filter."
            description="A description of the shortcut to toggle the filter for cards assigned to you"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['S']} messageId={'watch'}>
          <FormattedMessage
            id="templates.shortcuts.pressing-s-will-allow-you-to-watch-or-unwatch-a-card-watching-a-card-will-notify-you-of-actions-related-to-the-card"
            defaultMessage="Pressing “s” will allow you to watch or unwatch a card. Watching a card will notify you of actions related to the card."
            description="A description of the shortcut to watch or unwatch a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Space']} messageId="assign-self">
          <FormattedMessage
            id="templates.shortcuts.pressing-space-will-assign-or-unassign-yourself-to-a-card"
            defaultMessage="Pressing “space” will assign (or unassign) yourself to a card."
            description="A description of the shortcut to assign or unassign yourself from a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['T']} messageId="edit-title">
          <FormattedMessage
            id="templates.shortcuts.if-viewing-a-card-pressing-t-will-edit-the-title-if-hovering-over-a-card-pressing-t-will-open-the-card-and-edit-the-title"
            defaultMessage="If viewing a card, pressing “t” will edit the title. If hovering over a card, pressing “t” will open the card and edit the title."
            description="A description of the shortcut to edit the title of a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['V']} messageId="vote">
          <FormattedMessage
            id="templates.shortcuts.pressing-v-will-add-or-remove-your-vote-on-a-card-if-the-voting-power-up-is-enabled"
            defaultMessage="Pressing “v” will add (or remove) your vote on a card if the Voting Power-Up is enabled."
            description="A description of the shortcut to add or remove a vote on a card if the Voting Power-Up is enabled"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['X']} messageId="clear-all-filters">
          <FormattedMessage
            id="templates.shortcuts.use-x-to-clear-all-active-card-filters"
            defaultMessage="Use “x” to clear all active card filters."
            description="A description of the shortcut to clear all active card filters"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['?']} messageId="open-shortcuts-page">
          <FormattedMessage
            id="templates.shortcuts.pressing-will-open-the-shortcuts-page"
            defaultMessage="Pressing “?” will open the shortcuts page."
            description="A description of the shortcut to open the shortcuts page"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['@']} messageId="autocomplete-members">
          <FormattedMessage
            id="templates.shortcuts.when-writing-a-comment-you-can-type-at-plus-a-member-s-name-username-or-initials-and-get-a-list-of-matching-members-you-can-navigate-that-list-with-the-up-and-down-arrows-pressing-enter-or-tab-with-a-member-selected-will-mention-that-user-in-the-comment-the-mentioned-user-will-get-a-notification-once-submitted"
            defaultMessage="When writing a comment, you can type “@” plus a member’s name, username, or initials and get a list of matching members. You can navigate that list with the up and down arrows. Pressing enter or tab with a member selected will mention that user in the comment. The mentioned user will get a notification once submitted."
            description="A description of the shortcut to autcomplete members"
            tagName="p"
          />
        </Shortcut>

        <Shortcut
          keys={['Z']}
          messageId="undo-action"
          helpPageUrl="https://help.trello.com/article/1230-undoing-an-action-in-trello"
        >
          <FormattedMessage
            id="templates.shortcuts.pressing-z-will-undo-an-action"
            defaultMessage="Pressing “Z” will undo your last action on a card."
            description="A description of the shortcut to undo an action on a card"
            tagName="p"
          />
        </Shortcut>

        <Shortcut
          keys={['Shift', 'Z']}
          messageId="redo-action"
          helpPageUrl="https://help.trello.com/article/1230-undoing-an-action-in-trello"
        >
          <FormattedMessage
            id="templates.shortcuts.pressing-shift-z-will-redo-an-action"
            defaultMessage="After undoing an action, pressing “Shift + Z” will redo the last action that was undone."
            description="A description of the shortcut to redo an action"
            tagName="p"
          />
        </Shortcut>

        <Shortcut
          keys={['R']}
          messageId="repeat-action"
          helpPageUrl="https://help.trello.com/article/1231-repeating-an-action-in-trello"
        >
          <FormattedMessage
            id="templates.shortcuts.when-viewing-or-hovering-over-a-card-pressing-r-will-repeat-your-last-action"
            defaultMessage="When viewing or hovering over a card, pressing “R” will repeat your last action from a different card."
            description="A description of the shortcut to repeat an action"
            tagName="p"
          />
        </Shortcut>

        <Shortcut keys={['Shift', 'Scroll']} messageId="move-sideways">
          <FormattedMessage
            id="templates.shortcuts.use-shift-scroll-to-move-left-or-right"
            defaultMessage="Hold “Shift” while scrolling with your mouse to scroll left and right on a board."
            description="A description of the shortcut to scroll left and right on a board"
            tagName="p"
          />
        </Shortcut>
      </div>
    </div>
  );
};
